import { Component, Input, OnInit } from '@angular/core';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';

// Compact hero action row for plate-detail-v2 (Favourite / Follow / Share /
// Sell similar). Favourite reuses the existing capability; Follow is the
// account-v2 followed-plates system (a follow with no stated intent gets the
// default alerts: availability and price, daily digest).
@Component({
  selector: 'pla-pdv2-hero-actions',
  templateUrl: './hero-actions.component.html',
  styleUrls: ['./hero-actions.component.scss'],
})
export class HeroActionsComponent implements OnInit {
  @Input() public registration: string | null = null;
  @Input() public formattedRegistration: string | null = null;
  @Input() public searchTerm: string | null = null;
  @Input() public price: number | null = null;

  public shareState: 'idle' | 'copied' = 'idle';

  // Two-step unfollow so a follow can never be silently destroyed by a
  // stray click (§5: no silent destructive actions).
  public unfollowArmed = false;
  private unfollowTimer: any = null;

  constructor(
    private trackPlate: TrackPlateService,
    private savedPlates: SavedPlatesService
  ) {}

  ngOnInit(): void {
    // Loads the shared tracked set (and reloads it after sign-in).
    this.trackPlate.refresh(false);
  }

  public get isFollowed(): boolean {
    return this.trackPlate.isTracked(this.registration);
  }

  public get followLoading(): boolean {
    return this.trackPlate.isBusy(this.registration);
  }

  private canonicalReg(): string {
    return (this.registration || '').replace(/\s+/g, '').toUpperCase();
  }

  // Save = the personal shortlist (SavedPlatesService). No sign-in needed:
  // guests save in this browser and it merges into their account on sign-in.
  // Saving never creates alerts (that is Track plate).
  public get isFavourite(): boolean {
    return this.savedPlates.isSaved(this.registration);
  }

  public get favLoading(): boolean {
    return this.savedPlates.isBusy(this.registration);
  }

  public toggleFavourite(): void {
    if (!this.canonicalReg()) return;
    this.savedPlates.toggle(this.registration || '', this.searchTerm || undefined).subscribe();
  }

  // Track plate: the shared tracking system (TrackPlateService). Stopping
  // needs a second confirming click.
  public toggleFollow(): void {
    const reg = this.canonicalReg();
    if (reg === '') return;

    if (this.isFollowed) {
      if (!this.unfollowArmed) {
        this.unfollowArmed = true;
        clearTimeout(this.unfollowTimer);
        this.unfollowTimer = setTimeout(() => (this.unfollowArmed = false), 3500);
        return;
      }
      this.unfollowArmed = false;
      this.trackPlate.untrack(reg).subscribe();
      return;
    }

    this.trackPlate.track(reg, this.formattedRegistration || this.registration || '');
  }

  public get followLabel(): string {
    // Tracking covers availability and price (account-v2 follow underneath).
    if (this.unfollowArmed) return 'Stop tracking?';
    return this.isFollowed ? 'Tracking' : 'Track plate';
  }

  // Share the plate page. Prefers the native Web Share sheet; falls back to
  // copying the URL to the clipboard with a transient "Copied" state.
  public async share(): Promise<void> {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = this.formattedRegistration || this.registration || 'PlateX';
    const nav: any = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.share === 'function') {
      try {
        await nav.share({ title, text: title, url });
        return;
      } catch (e) {
        /* user cancelled or share unsupported — fall through to clipboard */
      }
    }
    if (nav && nav.clipboard && url) {
      try {
        await nav.clipboard.writeText(url);
        this.shareState = 'copied';
        setTimeout(() => (this.shareState = 'idle'), 1600);
      } catch (e) {
        /* clipboard blocked — nothing further we can safely do */
      }
    }
  }
}
